import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  BarChart3, 
  Users, 
  Plus, 
  Route, 
  MessageSquare, 
  Settings, 
  TrendingUp,
  Star,
  BookOpen,
  CreditCard,
  HelpCircle,
  Shield,
  FileText,
  LogOut,
  User,
  Home,
  Heart,
  Trophy,
  ChevronRight
} from 'lucide-react';
import { useUI } from '@/contexts/UIContext';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/components/auth/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useUserRole } from '@/hooks/useUserRole';
import { Button } from '@/components/ui/button';

interface MenuItem {
  icon: React.ElementType;
  label: string;
  path: string;
  role?: 'instructor' | 'student';
}

const menuItems: MenuItem[] = [
  // Student items
  { icon: User, label: 'Your Instructor', path: '/student/your-instructor', role: 'student' },
  { icon: Home, label: 'Dashboard', path: '/student-home', role: 'student' },
  { icon: BookOpen, label: 'DVSA Skills', path: '/dvsa-skills', role: 'student' },
  { icon: User, label: 'Profile', path: '/student-profile', role: 'student' },
  { icon: Heart, label: 'Mindfulness', path: '/mindfulness', role: 'student' },
  { icon: Trophy, label: 'Achievements', path: '/achievements', role: 'student' },
  
  // Instructor items
  { icon: BarChart3, label: 'Dashboard', path: '/instructor', role: 'instructor' },
  { icon: TrendingUp, label: 'Students', path: '/instructor/students', role: 'instructor' },
];

export const DrawerMenu: React.FC = () => {
  const { menuOpen, setMenuOpen } = useUI();
  const { profile } = useProfile();
  const { logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { role, loading: roleLoading } = useUserRole();
  const drawerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && menuOpen) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [menuOpen, setMenuOpen]);

  // Focus management
  useEffect(() => {
    if (menuOpen && drawerRef.current) {
      const firstFocusable = drawerRef.current.querySelector('button') as HTMLElement;
      firstFocusable?.focus();
    }
  }, [menuOpen]);

  // Touch gestures
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const deltaX = e.touches[0].clientX - touchStartX.current;
    // Right-to-left swipe closes drawer
    if (deltaX < -60) {
      setMenuOpen(false);
      touchStartX.current = null;
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await logout();
      setMenuOpen(false);
      navigate('/auth');
      toast({
        title: "Signed out successfully",
        description: "You have been logged out.",
      });
    } catch (error) {
      console.error('Sign out error:', error);
      // Force navigation even on error
      setMenuOpen(false);
      navigate('/auth');
    }
  };

  const filteredItems = menuItems.filter(item => 
    !item.role || item.role === profile?.role
  );

  const instructorItems = filteredItems.filter(item => item.role === 'instructor');
  const studentItems = filteredItems.filter(item => item.role === 'student');

  if (!menuOpen) return null;

  return (
    <>
      {/* Scrim */}
      <div
        className="fixed inset-0 bg-black/35 z-[1100]"
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />
      
      {/* Drawer */}
      <div
        ref={drawerRef}
        className="fixed left-0 top-0 h-screen w-[85%] max-w-[320px] sm:max-w-[360px] bg-card z-[1200] overflow-y-auto animate-[slide-in-from-left_0.3s_ease-out]"
        style={{
          boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
          borderRadius: '0 14px 14px 0',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="flex flex-col h-full">
          {/* Profile Header */}
          <div className="p-4 sm:p-6 border-b border-border">
            <div className="flex items-center space-x-2.5 sm:space-x-3 mb-2 sm:mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-semibold text-base sm:text-lg">
                  {profile?.first_name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground text-sm sm:text-base truncate">
                  {profile?.first_name} {profile?.last_name}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground capitalize truncate">
                  {profile?.role || 'User'}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleNavigation('/profile')}
              className="text-xs text-primary hover:underline flex items-center gap-1"
            >
              View Profile
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>

          {/* Navigation Content */}
          <div className="flex-1 p-3 sm:p-4">
            {!roleLoading && (role === 'instructor' || role === 'admin') && <div className="mb-4 border-b border-border pb-4">
              <Button variant="outline" className="w-full justify-start" onClick={() => handleNavigation('/instructor/private-offers')}><FileText />Intensive bookings · preview</Button>
            </div>}
            {/* Instructor Section */}
            {instructorItems.length > 0 && (
              <div className="mb-6">
                <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2 sm:mb-3 px-1">
                  Instructor
                </h4>
                <nav className="space-y-0.5 sm:space-y-1">
                  {instructorItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      className="w-full flex items-center space-x-2.5 sm:space-x-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-left transition-colors hover:bg-muted group"
                    >
                      <item.icon className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
                      <span className="text-sm sm:text-base text-muted-foreground group-hover:text-foreground transition-colors truncate">
                        {item.label}
                      </span>
                    </button>
                  ))}
                </nav>
                
                {/* Settings and My Profile for instructors */}
                {profile?.role === 'instructor' && (
                  <div className="mt-4 pt-3 border-t border-border space-y-1">
                    <button
                      onClick={() => handleNavigation('/instructor/account')}
                      className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-colors hover:bg-muted group"
                    >
                      <User className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                      <span className="text-muted-foreground group-hover:text-foreground transition-colors">Account</span>
                    </button>
                    <button
                      onClick={() => handleNavigation('/instructor/settings')}
                      className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-colors hover:bg-muted group"
                    >
                      <Settings className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                      <span className="text-muted-foreground group-hover:text-foreground transition-colors">Settings</span>
                    </button>
                    <button
                      onClick={() => handleNavigation('/profile')}
                      className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-colors hover:bg-muted group"
                    >
                      <User className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                      <span className="text-muted-foreground group-hover:text-foreground transition-colors">My Profile</span>
                    </button>
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-colors hover:bg-destructive/10 hover:text-destructive"
                    >
                      <LogOut className="h-5 w-5" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Divider */}
            {instructorItems.length > 0 && studentItems.length > 0 && (
              <div className="border-t border-border my-6" />
            )}

            {/* Student Section */}
            {studentItems.length > 0 && (
              <div className="mb-6">
                <nav className="space-y-1">
                  {studentItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-all duration-200 hover:bg-muted group relative ${
                          isActive ? 'bg-muted' : ''
                        }`}
                      >
                        {/* Blue accent bar for active page */}
                        {isActive && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary rounded-r-full" />
                        )}
                        <div className="flex items-center space-x-3">
                          <item.icon className={`h-5 w-5 transition-colors ${
                            isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                          }`} />
                          <span className={`font-semibold transition-colors ${
                            isActive ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground'
                          }`}>
                            {item.label}
                          </span>
                        </div>
                        <ChevronRight className={`h-4 w-4 transition-colors ${
                          isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                        }`} />
                      </button>
                    );
                  })}
                </nav>
                
                {/* Settings and Sign Out for students */}
                {profile?.role === 'student' && (
                  <div className="mt-4 pt-3 border-t border-border space-y-1">
                    <button
                      onClick={() => handleNavigation('/account-settings')}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-all duration-200 hover:bg-muted group relative ${
                        location.pathname === '/account-settings' ? 'bg-muted' : ''
                      }`}
                    >
                      {location.pathname === '/account-settings' && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary rounded-r-full" />
                      )}
                      <div className="flex items-center space-x-3">
                        <Settings className={`h-5 w-5 transition-colors ${
                          location.pathname === '/account-settings' ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                        }`} />
                        <span className={`font-semibold transition-colors ${
                          location.pathname === '/account-settings' ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground'
                        }`}>Settings</span>
                      </div>
                      <ChevronRight className={`h-4 w-4 transition-colors ${
                        location.pathname === '/account-settings' ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                      }`} />
                    </button>
                    
                    <div className="my-3 border-t border-border" />
                    
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-all duration-200 hover:bg-destructive/10 group"
                    >
                      <div className="flex items-center space-x-3">
                        <LogOut className="h-5 w-5 text-muted-foreground group-hover:text-destructive transition-colors" />
                        <span className="font-semibold text-muted-foreground group-hover:text-destructive transition-colors">Sign Out</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-destructive transition-colors" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border p-4">
            {/* Policies Section */}
            <div className="mb-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 px-1">
                Help & Policies
              </p>
              <div className="space-y-0.5">
                <button
                  onClick={() => handleNavigation('/policies/complaints')}
                  className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-left transition-colors hover:bg-muted text-sm"
                >
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Complaints Procedure</span>
                </button>
                <button
                  onClick={() => handleNavigation('/policies/safeguarding')}
                  className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-left transition-colors hover:bg-muted text-sm"
                >
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Safeguarding Policy</span>
                </button>
                <button
                  onClick={() => handleNavigation('/policies/edi')}
                  className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-left transition-colors hover:bg-muted text-sm"
                >
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">EDI Statement</span>
                </button>
                <button
                  onClick={() => handleNavigation('/policies/accessibility')}
                  className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-left transition-colors hover:bg-muted text-sm"
                >
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Accessibility</span>
                </button>
              </div>
            </div>
            
            <div className="border-t border-border pt-3 space-y-1">
              <button
                onClick={() => handleNavigation('/terms')}
                className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-left transition-colors hover:bg-muted text-sm"
              >
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Terms & Conditions</span>
              </button>
            </div>
            
          </div>
        </div>
      </div>
    </>
  );
};